/* eslint-disable react-hooks/exhaustive-deps */
import "./analyticsdashboard.css";

import { Redirect, Route, Switch, useLocation } from "react-router-dom";
import {
  hasAnalyticsRole,
  hasAnovaRole,
  hasFeaturesRole,
  hasPcaRole,
} from "../users/rolesUtil";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";

import { ANALYTICS_EVENTS } from "../constants";
import { AnalyticsUnavailable } from "./AnalyticsUnavailable";
import { Anova } from "./templates/Anova/Anova";
import { BreederReport } from "./templates/BreederReport";
import { Container } from "reactstrap";
import LoadingImg from "../components/loading";
import { PrincipalComponentAnalysis } from "./templates/PrincipalComponentAnalysis/PrincipalComponentAnalysis";
import { fetchFeaturesByDate } from "../actions/resultMap";
import { fetchTraitGroups } from "../actions/traits";
import { selectSelectedTrialContractInfo } from "../selectors/resultMap";
import { useEffectOnce } from "react-use";
import { useTracking } from "../analytics";

/*
 * Analytics tool: powerful analytics dashboards
 * Single page app embedded in cloverfield to display intelligence and analytics
 * about calculated traits and crops fields.
 * Powerful modular templates can be implemented and embedded here
 */
export const AnalyticsLayout = () => {
  const [
    trial,
    features,
    featuresByDate,
    refreshing,
    refreshingFeaturesByDate,
    featuresByDateFailed,
    featuresByDateLoaded,
    traitsListForMap,
  ] = useSelector((state) => [
    state.resultMap.trial,
    state.resultMap.features,
    state.resultMap.featuresByDate,
    state.resultMap.refreshing,
    state.resultMap.refreshingFeaturesByDate,
    state.resultMap.featuresByDateFailed,
    state.resultMap.featuresByDateLoaded,
    state.resultMap.traitsListForMap,
  ]);

  const selectedContract = useSelector(selectSelectedTrialContractInfo);

  const traitGroupsFetched = useSelector(
    (state) => state.traits.traitGroupsFetched
  );

  const dispatch = useDispatch();

  useEffectOnce(() => {
    if (!traitGroupsFetched) dispatch(fetchTraitGroups());
  });

  const { trackEvent } = useTracking();

  // PCA drawer state, held here because the PCA page remounts when a site
  // loads (key={featuresByDate}): picking a site must not shut it. Each arrival
  // on a page opens it only when no site is loaded yet, like the Breeder one.
  const { pathname } = useLocation();
  const [pcaDrawerOpen, setPcaDrawerOpen] = useState(!trial);
  const [prevPathname, setPrevPathname] = useState(pathname);
  if (pathname !== prevPathname) {
    setPrevPathname(pathname);
    setPcaDrawerOpen(!trial);
  }

  useEffect(() => {
    if (trial) {
      trackEvent(ANALYTICS_EVENTS.ANALYTICS_IS_BEING_RENDERED);
      // Fetch features by date on trial change. Without view_features the
      // backend answers 403, so it is not even asked. After a failure it is
      // not asked again either: the request would fail in a loop.
      if (
        !featuresByDateLoaded &&
        !refreshingFeaturesByDate &&
        !featuresByDateFailed &&
        hasFeaturesRole(selectedContract?.roles)
      )
        dispatch(fetchFeaturesByDate({ id: trial.id }));
    }
  }, [
    trial,
    featuresByDateLoaded,
    refreshingFeaturesByDate,
    featuresByDateFailed,
    selectedContract,
    dispatch,
    trackEvent,
  ]);

  return (
    <Container fluid className="page-content d-flex flex-column powerdash">
      {/* Change template here */}

      <Switch>
        <Route exact path="/analytics/breeder">
          {trial &&
            (!hasAnalyticsRole(selectedContract.roles) ||
              traitsListForMap.length === 0) && (
              <Redirect to="/analytics/unavailable?from=breeder" />
            )}
          <BreederReport />
        </Route>
        <Route exact path="/analytics/pca">
          {trial &&
            (!hasPcaRole(selectedContract.roles) ||
              traitsListForMap.length === 0) && (
              <Redirect to="/analytics/unavailable?from=pca" />
            )}
          <PrincipalComponentAnalysis
            key={featuresByDate}
            drawerOpen={pcaDrawerOpen}
            setDrawerOpen={setPcaDrawerOpen}
          />
        </Route>
        <Route exact path="/analytics/anova">
          {trial &&
            (!hasAnovaRole(selectedContract.roles) ||
              traitsListForMap.length === 0) && (
              <Redirect to="/analytics/unavailable?from=anova" />
            )}
          <Anova key={features} />
        </Route>
        <Route exact path="/analytics/unavailable">
          <AnalyticsUnavailable />
        </Route>
        <Redirect to="/analytics/breeder" />
      </Switch>

      {(refreshing || !traitGroupsFetched) && <LoadingImg visible />}
    </Container>
  );
};
